import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { message } from "antd";
import {
  IRoutingOrder,
  IRoutingOrderDetails,
  IRoutingOrderUpdate,
  IRoutingOrderVersion,
  IRoutingOrderVersionsList,
} from "./types/routingOrder";
import {
  getRousingOrderAgreements,
  getRousingOrderDeleteStatusRoute,
  getRousingOrderSearchRoute,
  getRousingOrderUpdateStatusRoute,
  getRousingOrderVersionsRoute,
} from "../../../lib/routes/routingOrders";
import loadingStatusCodes from "../../../lib/const/loadingStatusCodes";
import { LoadingStatusCode as ILoadingStatusCode } from "../../../lib/types/LoadingStatusCode";
import Message from "../../../components/Message/Message";
import { AnyObject } from "../../../lib/interfaces/AnyObject";
import { toDisplayFormat } from "../../../lib/utils/formatDate";
import { catchApiError } from "../../../lib/utils/catchApiError";

interface IRoutingOrdersState {
  routingOrdersList: IRoutingOrder[];
  routingOrdersTotalElements: number;
  routingOrdersListLoading: ILoadingStatusCode;
  routingOrdersVersions: IRoutingOrderVersionsList[];
  routingOrdersVersionsLoading: ILoadingStatusCode;
  routingOrderUpdated: IRoutingOrderUpdate | undefined;
  routingOrderUpdatedLoading: ILoadingStatusCode;
  deteleRoutingOrderLoading: ILoadingStatusCode;
  agreements: any[];
  agreementsLoading: ILoadingStatusCode;
}

const initialState: IRoutingOrdersState = {
  routingOrdersList: [],
  routingOrdersTotalElements: 0,
  routingOrdersListLoading: loadingStatusCodes.idle,
  routingOrdersVersions: [],
  routingOrdersVersionsLoading: loadingStatusCodes.idle,
  routingOrderUpdated: undefined,
  routingOrderUpdatedLoading: loadingStatusCodes.idle,
  deteleRoutingOrderLoading: loadingStatusCodes.idle,
  agreements: [],
  agreementsLoading: loadingStatusCodes.idle,
};

const fetchRousingOrderSearch = createAsyncThunk(
  "routing-orders/fetchRousingOrderSearch",
  async (searchParams: AnyObject) => {
    try {
      const { data } = await axios(getRousingOrderSearchRoute(searchParams));
      return data;
    } catch (e) {
      Message("Ошибка загрузки списка заказов. Попробуйте позже", e);
    }
  }
);

const fetchRoutingOrderVersions = createAsyncThunk(
  "routing-orders/fetchRoutingOrderVersions",
  async ({ routingOrderId }: { routingOrderId: number }) => {
    try {
      const { data } = await axios(getRousingOrderVersionsRoute(routingOrderId));
      return data;
    } catch (e) {
      Message("Ошибка загрузки версий заказа. Попробуйте позже", e);
    }
  }
);

const fetchUpdateRoutingOrderStatus = createAsyncThunk(
  "routing-orders/fetchUpdateRoutingOrderStatus",
  async ({ routingOrderId, orderStatusId }: { routingOrderId: number; orderStatusId: number }) => {
    try {
      const response = await axios.patch(getRousingOrderUpdateStatusRoute(), {
        id: routingOrderId,
        orderStatusId,
      });
      const { data } = await response;
      const { status } = await response;
      Message("Статус заказа изменен", status);
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка при отправке",
      });
    }
  }
);

const fetchDeleteRoutingOrder = createAsyncThunk(
  "routing-orders/fetchDeleteRoutingOrder",
  async (routingOrderId: number) => {
    try {
      const response = await axios.delete(getRousingOrderDeleteStatusRoute({ id: routingOrderId }));
      const { data } = await response;
      const { status } = await response;
      Message("Заказ удален", status);
      return data;
    } catch (e) {
      // return e
      return catchApiError({
        e,
        title: "Ошибка при удалении",
      });
    }
  }
);

const fetchAgreements = createAsyncThunk(
  "routing-orders/fetchAgreements",
  async (accountId: number) => {
    try {
      const response = await axios.get(getRousingOrderAgreements({ accountId }));
      const { data } = await response;
      return data;
    } catch (e) {
      Message("Ошибка загрузки списка заказов. Попоробуйте позже", e);
    }
  }
);

const getConcatValues = (path, name) => {
  let result = "";
  if (path) {
    result = path;
  }
  if (name) {
    if (result) {
      result = `${result} - `;
    }
    result = `${result}${name}`;
  }
  return result;
};

const slice = createSlice({
  name: "routingOrders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRousingOrderSearch.pending, (state, action) => {
        state.routingOrdersListLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchRousingOrderSearch.fulfilled, (state, action) => {
        const _routingOrders: IRoutingOrder[] = action.payload?.content?.map((item) => {
          let currentProportionGroup = -1;
          const _routingItems: IRoutingOrderDetails[] = item.routingItems.map((elem, idx) => {
            let { proportionRatio } = elem;
            if (idx === 0) {
              currentProportionGroup = elem.proportionGroup;
            } else if (elem.proportionGroup === currentProportionGroup) {
              proportionRatio = undefined;
            }

            const _routingDetail: IRoutingOrderDetails = {
              id: idx,
              key: idx,
              proportionRatio,
              organization: elem?.toOffering?.agreement?.account?.organization?.fullName || "",
              offeringName: elem?.toOffering?.name || "",
              rateMax: elem?.rateMax,
              currencyId: elem?.toOffering?.currencyId,
              currencyName: "",
            };
            return _routingDetail;
          });
          const _routingOrder: IRoutingOrder = {
            id: item.id,
            destination: getConcatValues(
              item?.tcOfferingItem?.destination?.path,
              item?.tcOfferingItem?.destination?.name
            ),
            abbrev: getConcatValues(
              item?.tcOfferingItem?.destination?.shortPath,
              item?.tcOfferingItem?.destination?.shortName
            ),
            offeringName: item?.tcOfferingItem?.offeringName,
            orderStatusId: item?.orderStatusId || 0,
            orderStatusName: "",
            datePeriod: `${toDisplayFormat(item.fromDate)} - ${toDisplayFormat(item.toDate)}`,
            toDate: item.toDate,
            fromDate: item.fromDate,
            routingItems: _routingItems,
            isOpenDetails: false,
            isOpenVersions: false,
            loadingVersions: loadingStatusCodes.idle,
            key: item.id,
            categoryId: item?.tcOfferingItem?.destination?.categoryId,
          };
          return _routingOrder;
        });
        state.routingOrdersList = _routingOrders;
        state.routingOrdersTotalElements = action.payload?.totalElements;
        state.routingOrdersListLoading = loadingStatusCodes.fulfilled;
      })

      .addCase(fetchRoutingOrderVersions.fulfilled, (state, action) => {
        const _routingOrdersVersions: IRoutingOrderVersionsList[] = [];
        const _versionList: IRoutingOrderVersion[] = [];

        Array.isArray(action.payload) &&
          action.payload.forEach((item) => {
            const _routingOrdersVersion: IRoutingOrderVersion = {
              id: item.id,
              key: item.id,
              destination: getConcatValues(
                item?.tcOfferingItem?.destination?.path,
                item?.tcOfferingItem?.destination?.name
              ),
              abbrev: getConcatValues(
                item?.tcOfferingItem?.destination?.shortPath,
                item?.tcOfferingItem?.destination?.shortName
              ),
              offeringName: item?.tcOfferingItem?.offeringName,
              orderStatusId: item?.orderStatusId || 0,
              orderStatusName: "",
              toDate: toDisplayFormat(item.toDate),
              fromDate: toDisplayFormat(item.fromDate),
              datePeriod: `${toDisplayFormat(item.fromDate)} - ${toDisplayFormat(item.toDate)}`,
              categoryId: item?.tcOfferingItem?.destination?.categoryId,
            };
            _versionList.push(_routingOrdersVersion);
          });

        _routingOrdersVersions.push({
          id: action.meta.arg.routingOrderId,
          list: _versionList,
        });

        state.routingOrdersVersions = _routingOrdersVersions;
        state.routingOrdersVersionsLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchRoutingOrderVersions.pending, (state) => {
        state.routingOrdersVersionsLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchUpdateRoutingOrderStatus.pending, (state) => {
        state.routingOrderUpdatedLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchUpdateRoutingOrderStatus.fulfilled, (state, { payload }) => {
        if (payload) {
          const _routingOrdersVersion: IRoutingOrderUpdate = {
            id: payload?.id,
            orderStatusId: payload?.orderStatusId,
            fromDate: toDisplayFormat(payload?.fromDate),
            toDate: toDisplayFormat(payload?.toDate),
          };
          state.routingOrderUpdated = _routingOrdersVersion;
        }
        state.routingOrderUpdatedLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchDeleteRoutingOrder.pending, (state) => {
        state.deteleRoutingOrderLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchDeleteRoutingOrder.fulfilled, (state) => {
        state.deteleRoutingOrderLoading = loadingStatusCodes.fulfilled;
      })
      .addCase(fetchDeleteRoutingOrder.rejected, (state, action) => {
        state.deteleRoutingOrderLoading = loadingStatusCodes.rejected;
      })
      .addCase(fetchAgreements.pending, (state) => {
        state.agreementsLoading = loadingStatusCodes.pending;
      })
      .addCase(fetchAgreements.fulfilled, (state, action) => {
        state.agreementsLoading = loadingStatusCodes.fulfilled;
        state.agreements = action.payload;
      });
  },
});

const { reducer } = slice;

export {
  reducer,
  fetchRousingOrderSearch,
  fetchRoutingOrderVersions,
  fetchUpdateRoutingOrderStatus,
  fetchDeleteRoutingOrder,
  fetchAgreements,
};
